/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:03]
Permissions to update outside the main branch: [true]
*/

import { Button, Form, Modal, Pending, requiredRule, ValidatedInput } from '@finbeat/react-toolkit';
import { getProps } from 'axios/propApi';
import { BOProps } from 'base-components/BOProps/BOProps';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { isNil } from 'lodash';
import { PropExtended } from 'models/api/prop';
import {
    TariffGroup,
    TariffGroupCreate,
    TariffGroupCreditProductUpdate,
} from 'models/api/tariffGroup';
import { CreditProductsProvider, useCreditProductsList } from 'providers/CreditProductsProvider';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import TariffGroupCreditProductsList from './TariffGroupCreditProductsList';
import { useTariffGroupsWorkplaceState } from './TariffGroups';

type TariffGroupChangeModalProps = {
    isOpen: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

const TariffGroupChangeModal = (p: TariffGroupChangeModalProps) => {
    const { isOpen = false, toggle } = p;
    const api = useApi();
    const { t } = useTranslation();
    const { hasRole } = useAuth();

    const { currentItem, changeItemMutation, addItemMutation } = useTariffGroupsWorkplaceState();

    const [itemProps, setItemProps] = useState<PropExtended[]>([...(currentItem?.props || [])]);

    const { isCreditProductsFetching, isTariffGroupCreditProductPropsFetching } =
        useCreditProductsList();

    useQuery('TariffGroupProps', () => getProps(api, 'TariffGroup'), {
        onSuccess: (props) => {
            const prop = { ...props[0], ...currentItem?.props[0] };
            setItemProps([prop]);
            const p = props.map((x) =>
                x.code === 'TariffComission'
                    ? {
                          ...x,
                          min: 0,
                          max: 100,
                          validate: (value: any) => {
                              if (!/^(?=.*\d)\d*(?:\.\d{0,2})?$/.test(value?.toString()))
                                  return 'BackOfficeOnlyTwoDigitsAfterDot';
                          },
                      }
                    : x
            );
            props && setItemProps(p);
        },
        enabled: isOpen && !itemProps.length,
    });

    const saveChanges = async (data: TariffGroup) => {
        const {
            isActive,
            name = '',
            dsc = '',
            props = [],
            tariffGroupCreditProducts = [],
        } = data || {};

        const formattedItem: TariffGroupCreate = {
            isActive: isActive,
            name: name,
            dsc: dsc,
            tariffGroupCreditProducts: tariffGroupCreditProducts
                .filter((cp) => currentItem || !cp.isDeleted)
                .map((t) => {
                    t.props.forEach((p) => {
                        if (p.values) {
                            p.values = p.values
                                // remove empty values
                                .filter((v) => v && !isNil(v.value))
                                .map((item) => ({ ...item, value: item.value?.toString() || '' }));
                        }
                    });

                    const tariffGroupCreditProductUpdate: TariffGroupCreditProductUpdate = {
                        id: t.id,
                        tariffGroupId: t.tariffGroupId,
                        creditProductId: t.creditProduct?.id || null,
                        isBase: t.isBase,
                        minSum: t.minSum,
                        maxSum: t.maxSum,
                        props: t.props,
                        isDeleted: t.isDeleted,
                    };
                    return tariffGroupCreditProductUpdate;
                }),
            props: props.map((p) => {
                if (p.values) {
                    p.values = p.values
                        .filter((v) => v && !isNil(v.value))
                        .map((item) => ({ ...item, value: item.value?.toString() || '' }));
                }

                return p;
            }),
        };

        currentItem
            ? changeItemMutation.mutate({ ...formattedItem, id })
            : addItemMutation.mutate({ ...formattedItem });

        toggle(false);
    };

    const closeModal = () => {
        toggle(false);
    };

    const { id = '', isActive = false, name = '', dsc = '' } = currentItem || {};

    const disabled = useMemo(
        () => (currentItem ? !hasRole('tariffgroupsUpdate') : !hasRole('tariffgroupsCreate')),
        [hasRole]
    );

    useEffect(
        () => setItemProps((prev) => prev.map((x) => ({ ...x, disabled: disabled }))),
        [disabled]
    );

    const itemTariffGroupCreditProducts = useMemo(
        () => [
            ...(currentItem?.tariffGroupCreditProducts.map((x) => ({
                ...x,
                props: x.props.map((p) => ({ ...p, disabled: disabled })),
            })) || []),
        ],
        [disabled]
    );

    const defaultvalues = {
        name,
        id,
        isActive,
        dsc,
        props: itemProps,
        tariffGroupCreditProducts: itemTariffGroupCreditProducts,
    };

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={() => closeModal()}
            className="tariff-group__modal"
            scrollable
            data-testid="tariff-group-modal"
        >
            {isCreditProductsFetching || isTariffGroupCreditProductPropsFetching ? (
                <Pending />
            ) : (
                <Form<TariffGroup>
                    onSubmit={saveChanges}
                    defaultValues={defaultvalues}
                    className="form-in-modal"
                >
                    <ModalHeader toggle={() => closeModal()}>
                        {currentItem ? `#${currentItem.id}` : t('BackOfficeNew')}
                    </ModalHeader>

                    <ModalBody className="tariffgroup__modal-body custom-scrollbar">
                        <Row>
                            <Col>
                                <ValidatedInput<TariffGroupCreate>
                                    type="checkbox"
                                    name="isActive"
                                    label={t('BackOfficeIsActive')}
                                    disabled={disabled}
                                />
                            </Col>
                        </Row>
                        <hr />
                        <Row>
                            <Col>
                                <ValidatedInput<TariffGroupCreate>
                                    name="name"
                                    rules={{
                                        required: requiredRule,
                                    }}
                                    label={t('BackOfficeName')}
                                    disabled={disabled}
                                />
                            </Col>
                        </Row>

                        <BOProps<TariffGroupCreate> name="props" />
                        <hr />
                        <TariffGroupCreditProductsList disabled={disabled} id={parseInt(`${id}`)} />
                        <hr />

                        <Row>
                            <Col>
                                <ValidatedInput<TariffGroupCreate>
                                    name="dsc"
                                    type="textarea"
                                    label={t('BackOfficeDescription')}
                                    disabled={disabled}
                                />
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => closeModal()}>
                            <Trans i18nKey="BackOfficeCancel" />
                        </Button>
                        <Button color="success" disabled={disabled} type={'submit'}>
                            <Trans i18nKey="BackOfficeSave" />
                        </Button>
                    </ModalFooter>
                </Form>
            )}
        </Modal>
    );
};

const TariffGroupChangeModalWrapped = (props: TariffGroupChangeModalProps) => (
    <CreditProductsProvider>
        <TariffGroupChangeModal {...props} />
    </CreditProductsProvider>
);

export default TariffGroupChangeModalWrapped;
